/**
 * Location Privacy Utilities
 *
 * Provides fuzzing of player locations so that the map never reveals exact
 * positions. Same offset is used at every zoom level (no zoom-dependent behavior).
 *
 * - Other users: deterministic position in the ring between 200m and 300m
 *   from their actual location (never closer than 200m, never farther than 300m).
 * - Current user ("You"): random offset within 200m, recomputed when location updates.
 */

const EARTH_RADIUS_M = 6371000;
const DEFAULT_FUZZ_RADIUS_M = 200;

/** For other users: marker must be at least this far from real location (meters). */
const FUZZ_MIN_RADIUS_M = 200;
/** For other users: marker must be at most this far from real location (meters). */
const FUZZ_MAX_RADIUS_M = 300;

// ── Simple deterministic hash for a string (returns a positive integer) ──
const hashString = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

/**
 * Compute a destination point from (lat, lng) given distance in meters and bearing in degrees.
 * Uses spherical earth model. Angles in degrees; result in degrees.
 */
const destinationPoint = (latDeg, lngDeg, distanceM, bearingDeg) => {
  const lat1 = (latDeg * Math.PI) / 180;
  const lng1 = (lngDeg * Math.PI) / 180;
  const brg = (bearingDeg * Math.PI) / 180;
  const dist = distanceM / EARTH_RADIUS_M;

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(dist) +
      Math.cos(lat1) * Math.sin(dist) * Math.cos(brg)
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(brg) * Math.sin(dist) * Math.cos(lat1),
      Math.cos(dist) - Math.sin(lat1) * Math.sin(lat2)
    );

  let lngDeg2 = (lng2 * 180) / Math.PI;
  while (lngDeg2 > 180) lngDeg2 -= 360;
  while (lngDeg2 < -180) lngDeg2 += 360;

  return {
    latitude: (lat2 * 180) / Math.PI,
    longitude: lngDeg2,
  };
};

/**
 * Return a fuzzed { latitude, longitude } for another user, in the ring between
 * minRadius (200m) and maxRadius (300m). Marker is never inside the 200m circle
 * and never outside the 300m circle. Same result at every zoom level.
 * Uses userId to pick a deterministic angle and distance so the pin doesn't jump.
 *
 * @param {number} lat     – real latitude
 * @param {number} lng     – real longitude
 * @param {string} userId  – stable identifier (UUID)
 * @param {number} [minRadiusM=200] – minimum distance in meters (default 200)
 * @param {number} [maxRadiusM=300] – maximum distance in meters (default 300)
 * @returns {{ latitude: number, longitude: number }}
 */
export const getFuzzedLocation = (lat, lng, userId, minRadiusM = FUZZ_MIN_RADIUS_M, maxRadiusM = FUZZ_MAX_RADIUS_M) => {
  if (!lat || !lng || !userId) return { latitude: lat, longitude: lng };

  const h1 = hashString(userId + '_bearing');
  const h2 = hashString(userId + '_dist');
  const bearing = (h1 % 360); // 0–359 degrees
  // Distance in [minRadiusM, maxRadiusM] – deterministic from userId
  const fraction = (h2 % 1000) / 1000;
  const distance = minRadiusM + fraction * (maxRadiusM - minRadiusM);

  return destinationPoint(lat, lng, distance, bearing);
};

/**
 * Return a random point within the given radius (meters) of (lat, lng).
 * Used for the current user's "You" marker so their displayed position
 * is never exact. Call whenever location is updated.
 *
 * @param {number} lat        – real latitude
 * @param {number} lng        – real longitude
 * @param {number} [radiusM=200] – radius in meters
 * @returns {{ latitude: number, longitude: number }}
 */
export const getRandomPointWithinRadius = (lat, lng, radiusM = DEFAULT_FUZZ_RADIUS_M) => {
  if (!lat || !lng || radiusM <= 0) return { latitude: lat, longitude: lng };

  const bearing = Math.random() * 360;
  const distance = Math.sqrt(Math.random()) * radiusM; // uniform in area

  return destinationPoint(lat, lng, distance, bearing);
};
